"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.api = void 0;
const baseUri = process.env.VUE_APP_API_BASE_URL || (window.location.hostname === 'localhost' ? 'http://localhost:30017/manage/' : 'https://giraffeears.com/ear/manage/');
function AppendObject2UrlParams(url, params) {
    let keys = Object.keys(params);
    if (keys.length == 0)
        return url;
    let tempUrl = '';
    let hasParams = url.indexOf('?') !== -1;
    tempUrl = Object.keys(params).reduce(function (before, after) {
        return before + after + '=' + encodeURIComponent(params[after]) + '&';
    }, tempUrl);
    tempUrl = tempUrl.substring(0, tempUrl.length - 1);
    return hasParams ? (url + '&' + tempUrl) : (url + '?' + tempUrl);
}
class API {
    constructor() {
        this._supabase = null;
        this._supabaseUrl = '';
        this._supabaseKey = '';
    }
    setSupabaseClient(client, url, key) {
        this._supabase = client;
        this._supabaseUrl = url;
        this._supabaseKey = key;
        console.log("Supabase client and credentials injected into API");
    }
    isWxMiniProgram() {
        return !!(navigator.userAgent.match(/miniProgram/i) || window.__wxjs_environment === 'miniProgram');
    }
    async _request(url, data) {
        if (API._openid && data && !data.openid) {
            data.openid = API._openid;
        }
        let headers = {
            'Content-Type': 'application/json'
        };
        // Add Authorization header if Supabase session is available
        if (this._supabase) {
            try {
                const { data: { session } } = await this._supabase.auth.getSession();
                if (session && session.access_token) {
                    headers['Authorization'] = `Bearer ${session.access_token}`;
                }
            } catch (e) {
                console.warn("Failed to get Supabase session for API request:", e);
            }
        }
        let postData = {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(data)
        };
        const response = await fetch(url, postData);
        if (!response.ok) {
            const message = `An error has occured: ${response.status}`;
            throw new Error(message);
        }
        const json = await response.json();
        return json;
    }

    async _syncToSupabase(table, data, updateId = null) {
        if (!this._supabaseUrl || !this._supabaseKey) return;

        let token = this._supabaseKey;
        let auth_uid = null;
        if (this._supabase) {
            try {
                const { data: { session } } = await this._supabase.auth.getSession();
                if (session?.access_token) {
                    token = session.access_token;
                    auth_uid = session.user.id;
                }
            } catch (e) {
                console.warn("Could not get Supabase session for sync:", e);
            }
        }

        // Force the data.user_id to match the authenticated session's UID
        // if we have one. This is critical for RLS policies to pass.
        if (auth_uid && data.user_id) {
            data.user_id = auth_uid;
        }

        const url = `${this._supabaseUrl}/rest/v1/${table}${updateId ? `?external_id=eq.${updateId}` : ''}`;
        const method = updateId ? 'PATCH' : 'POST';

        const headers = {
            'apikey': this._supabaseKey,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
        };

        try {
            const response = await fetch(url, {
                method: method,
                headers: headers,
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const errorBody = await response.text();
                console.error(`Supabase sync error (${response.status}):`, errorBody);
            } else {
                console.log(`Supabase ${table} sync success (${method})`);
            }
        } catch (err) {
            console.error(`Supabase fetch failed:`, err);
        }
    }
    static _saveSession() {
        localStorage.setItem("openid", API._openid);
        localStorage.setItem("session", JSON.stringify(API._session));
    }
    static loadSession() {
        API._openid = localStorage.getItem("openid") || '';
        API._session = JSON.parse(localStorage.getItem("session") || '{}');
    }
    static async _onLogin(resp) {
        if (resp.code) {
            console.debug("login failed", resp);
            return API._onLogout();
        }
        let user = JSON.parse(JSON.stringify(resp.data));
        // user.id = user.uid; // Do not overwrite short legacy id with long uuid
        API._openid = user.uid;
        API._session['user'] = user;
        API._saveSession();
        API._func_onAuthStateChange.forEach(f => {
            try {
                f('login', API._session);
            }
            catch (e) {
                console.info("login exception", e);
            }
        });
    }
    static async _onLogout() {
        API._openid = '';
        API._session = {};
        API._saveSession();
        // Clear all auth-related localStorage keys just in case
        localStorage.removeItem("openid");
        localStorage.removeItem("session");
        
        API._func_onAuthStateChange.forEach(f => {
            try {
                f('logout', API._session);
            }
            catch (e) {
                console.info("logout exception", e);
            }
        });
    }
    session() {
        return API._session;
    }
    user() {
        var _a;
        return ((_a = API._session) === null || _a === void 0 ? void 0 : _a.user) || null;
    }
    async userInfo() {
        if (!API._openid)
            return null;
        let res = await this._request(baseUri + 'UserInfo', {});
        if (!res || !res.data) {
            return null;
        }
        if (API._session) {
            this.updateUserInfo(res.data);
        }
        return res.data;
    }
    onAuthStateChange(func) {
        API._func_onAuthStateChange.push(func);
    }
    onChargingDone(func) {
        API._func_onChargingDone = func;
    }
    updateUserInfo(ui) {
        if (!API._session || !API._session.user)
            return;
        Object.assign(API._session.user, ui);
        API._session.user.lastUpdate = new Date();
    }
    async signUp({ email, password, recm }) {
        if (!email || !password) {
            return { code: 1, msg: 'invalid parameters' };
        }
        if (!recm)
            recm = '';
        let res = await this._request(baseUri + 'Register', { mail: email, pwd: password, recm });
        if (!res) {
            return null;
        }
        return res;
    }
    async signIn({ email, password }) {
        let res = await this._request(baseUri + 'Login', { mail: email, pwd: password });
        if (!res) {
            API._onLogout();
            return null;
        }
        API._onLogin(res);
        return res;
    }
    async signInWithCode(code, recm) {
        if (!code) {
            console.error("opened in wechat, but no code parameter");
            API._onLogout();
            return null;
        }
        if (!recm)
            recm = '';
        let apiResp = await this._request(baseUri + 'WxLogin', { openid: code, recm });
        if (apiResp && apiResp.code == 0) {
            console.log('wxlogin succeed:', apiResp);
            API._onLogin(apiResp);
        }
        else {
            console.error("WxLogin failed:", apiResp);
            API._onLogout();
        }
        return apiResp;
    }
    async signOut() {
        let res = null;
        try {
            res = await this._request(baseUri + 'Logout', {});
        } catch (e) {
            console.warn("Logout request failed, proceeding to clear local session", e);
        } finally {
            await API._onLogout();
        }
        return res;
    }
    async forgotPwd(email) {
        if (!email) {
            return { code: 1, msg: 'invalid parameters' };
        }
        let res = await this._request(baseUri + 'ForgotPassword', { mail: email });
        if (!res) {
            return null;
        }
        return res;
    }
    async feedback(feedback) {
        let res = await this._request(baseUri + 'Feedback', { feedback });
        if (!res) {
            return null;
        }
        if (res.code == 0 && res.hornsChanged) {
            this.userInfo().then((ui) => {
                if (ui)
                    API._saveSession();
            });
        }
        return res;
    }
    async userTrackInsert(history) {
        let res = await this._request(baseUri + 'UserTrackInsert', { history: JSON.stringify(history) });
        console.log("userTrackInsert", res);

        // Sync to Supabase via direct fetch
        if (this.user()?.uid) {
            this._syncToSupabase('user_tracks', {
                external_id: res?.id?.toString() || null,
                user_id: this.user().uid,
                event_type: 'chat',
                history: history,
                created_at: new Date().toISOString()
            });
        }

        if (res && res.code == 0) {
            this.userInfo().then((ui) => {
                if (ui)
                    API._saveSession();
            });
        }
        return res;
    }
    async userTrackUpdate(id, history, ended_at) {
        var _a;
        let feedback = 0;
        if (history.feedback && Array.isArray(history.feedback) && history.feedback.length > 0) {
            feedback = ((_a = history.feedback) === null || _a === void 0 ? void 0 : _a.filter(n => n).length) ? 1 : 0;
        }
        let res = await this._request(baseUri + 'UserTrackUpdate', { id, history: JSON.stringify(history), ended_at, feedback });

        // Sync to Supabase via direct fetch
        if (this.user()?.uid) {
            this._syncToSupabase('user_tracks', {
                user_id: this.user().uid,
                event_type: 'chat',
                history: history,
                ended_at: ended_at,
                feedback: feedback
            }, id);
        }

        if (!res) {
            return null;
        }
        if (res.code == 0 && res.hornsChanged) {
            this.userInfo().then((ui) => {
                if (ui)
                    API._saveSession();
            });
        }
        return res;
    }
    async UserTrackQuery(id) {
        let prms = id ? { id } : {};
        let res = await this._request(baseUri + 'UserTrackQuery', prms);
        if (!res || res.code) {
            console.error("UserTrackQuery response:", res);
            return null;
        }
        res.history = JSON.parse(res.history);
        return res;
    }
    async UserInfoUpdate(userInfo) {
        let res = await this._request(baseUri + 'UserInfoUpdate', userInfo);
        if (!res) {
            return null;
        }
        if (res.code == 0 && API._session && API._session.user) {
            this.updateUserInfo(userInfo);
            API._saveSession();
        }
        return res;
    }
    async ai_req(serviceid, data) {
        if (data && !data.uid) {
            data.uid = this.user().uid;
        }
        if (!(data === null || data === void 0 ? void 0 : data.uid)) {
            return { data: null, error: new Error("unknown user") };
        }
        let res = await this._request(baseUri + 'AI', { prm: data, sid: serviceid });
        if (!res || !res.data) {
            return { data: null, error: new Error("查询失败") };
        }
        return { data: res.data, error: null };
    }
    async hornsList(pg, pgsize) {
        let res = await this._request(baseUri + 'HornsList', { pg, pgsize });
        if (res) {
            return res;
        }
        console.error("failed to get HornsList");
        return null;
    }
    async hornsTransfer(peer, amount) {
        let res = await this._request(baseUri + 'HornsTransfer', { peer, amount });
        if (res && res.code == 0 && res.data) {
            this.updateUserInfo(res.data);
            API._saveSession();
            return "";
        }
        console.error("failed to get HornsTransfer");
        return res.msg || "请求失败";
    }
    async handleHashChange() {
        if (!this.isWxMiniProgram()) {
            return;
        }
        if (!this.session) {
            API.loadSession();
        }
        if (!this.session || !this.session['charging']) {
            return;
        }
        let params = {};
        window.location.hash.substring(1).split("&").forEach(function (pair) {
            let tmp = pair.split("=");
            if (tmp.length < 1)
                return;
            params[decodeURIComponent(tmp[0].trim())] = decodeURIComponent(tmp[1].trim());
        });
        console.debug("page hash:", params);
        let result = params['payresult'];
        if (!result) {
            return;
        }
        let succeed = result == 'success';
        if (API._func_onChargingDone) {
            try {
                API._func_onChargingDone(succeed);
            }
            catch (e) {
                console.error("produce onChargingDone failed", e, succeed);
            }
        }
        this.session['charging'] = false;
        console.debug("page hash:", params, succeed);
    }
    async charge(fee, ptype) {
        if (ptype != 'novpay' && ptype != 'stripe') {
            console.error("not implemented pay platform", ptype);
            return null;
        }
        let term = this.isWxMiniProgram() ? 'jsapi' : 'h5';
        let res = await this._request(baseUri + 'Charge', { fee, ptype, term });
        if (!res) {
            console.error("call api Charge failed", fee, ptype, term);
            return null;
        }
        if (res.code || (!res.data && !res.url)) {
            console.error("call api Charge failed", JSON.stringify(res, null, 2));
            return res;
        }
        if (res.cid) this.session['chargeid'] = res.cid;
        this.session['charging'] = true;
        API._saveSession();
        if (ptype == 'novpay') {
            const WeChatMiniProgramPayUrl = "/pages/payment/index";
            let url = AppendObject2UrlParams(WeChatMiniProgramPayUrl, res.data);
            console.log("wechatpay url", url);
            wx.miniProgram.navigateTo({
                url,
                sussess: () => { console.log("navigateTo success"); }
            });
        }
        return res;
    }
    async getChargeInfo(chargeid) {
        if (!chargeid) {
            chargeid = this.session['chargeid'];
        }
        if (!chargeid) {
            console.error("invalid chargeid");
            return "invalid chargeid";
        }
        let res = await this._request(baseUri + 'QryCharge', { cid: chargeid });
        if (res && res.amount) {
            return "";
        }
        console.error("failed to get ChargeInfo");
        return res.msg || '';
    }
    getBaseUri() {
        return baseUri;
    }
}
API._openid = '';
API._session = null;
API._func_onAuthStateChange = [];
API._func_onChargingDone = (success) => { console.log("OnChargeDone", success); };
API.loadSession();
exports.api = new API();
//# sourceMappingURL=api.js.map